<template>
  <div>
    <div class="grid">
      <div
        v-for="donut in donuts"
        :key="donut.key"
        :class="['col-12 sm:col-6', donutWidth]"
      >
        <PanelSkeleton v-if="store.isLoading(donut.loaderKey)" />

        <Card v-else>
          <template #title>
            <div class="text-lg font-semibold flex align-items-center">
              Revenue Per {{ donut.title }}
              <InfoIcon :text="donut.info" />
            </div>
          </template>
          <template #content>
            <pie-chart
              v-if="donut.data.length"
              :data="donut.data"
              :donut="true"
              prefix="$"
              :legend="false"
            />
            <div v-else class="chart-empty text-sm text-bluegray-500">
              {{ SNIPPET_EMPTY_MESSAGE }}
            </div>
          </template>
        </Card>
      </div>
    </div>

    <PanelSkeleton
      v-if="store.isLoading('daily')"
      class="my-3"
      height="26rem"
    />

    <Card v-else class="my-3">
      <template #title>
        <div class="text-lg font-semibold flex align-items-center">
          Snippet Daily
          <InfoIcon v-if="ratioNote" :text="ratioNote" />
        </div>
      </template>
      <template #content>
        <DataTable
          :value="store.daily.rows"
          :total-records="store.daily.totalRecords"
          :rows="rowsPerPage"
          class="p-datatable-striped p-datatable-sm"
          paginator
          lazy
          @page="onTableChange"
          @sort="onTableChange"
        >
          <Column
            v-for="col in columns"
            :key="col.field"
            :field="col.field"
            :header="col.header"
            sortable
          >
            <template #body="{ data }">
              {{ col.format(data[col.field]) }}
            </template>
          </Column>
          <template #empty>
            <span class="text-sm text-bluegray-500">{{
              SNIPPET_EMPTY_MESSAGE
            }}</span>
          </template>
          <template #paginatorend>
            <download-csv :data="store.daily.rows" class="download-csv">
              <Button
                type="button"
                icon="pi pi-download"
                text
                :disabled="store.isLoading('daily')"
              />
            </download-csv>
          </template>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import Card from "primevue/card";
import InfoIcon from "@/components/SnippetDashboard/InfoIcon.vue";
import PanelSkeleton from "@/components/SnippetDashboard/PanelSkeleton.vue";
import { formatMoney } from "@/helpers";
import { SNIPPET_EMPTY_MESSAGE } from "@/consts";
import { useSnippetRevenueStore } from "@/stores/snippetRevenue";

const store = useSnippetRevenueStore();

const rowsPerPage = ref(10);

const count = (value) => formatMoney(value, "decimal");
const money = (value) => formatMoney(value);
const optional = (value) => (value === null ? "—" : formatMoney(value));

const toDonut = (rows) =>
  rows.map((row) => [row.isOther ? "Other" : row.label || "—", row.revenue]);

const REMAINDER_NOTE = "Top 10 by revenue, the rest grouped as Other.";

const DONUTS = {
  domain: { title: "Domain", info: REMAINDER_NOTE },
  geo: { title: "Geo", info: REMAINDER_NOTE },
  buyer: { title: "Buyer", info: `Excludes loads. ${REMAINDER_NOTE}` },
  show: { title: "Show", info: `Excludes loads. ${REMAINDER_NOTE}` },
};

const donutWidth = computed(() =>
  store.dimensions.length === 3 ? "lg:col-4" : "lg:col-3"
);

const donuts = computed(() =>
  store.dimensions.map((key) => ({
    key,
    ...DONUTS[key],
    loaderKey: `breakdown:${key}`,
    data: toDonut(store.breakdown[key]),
  }))
);

const COLUMNS = {
  date: { header: "Date", format: (value) => value },
  loads: { header: "Loads", format: count },
  impressions: { header: "Ad Impressions", format: count },
  revenue: { header: "Revenue", format: money },
  cost: { header: "Cost", format: money },
  profit: { header: "Profit", format: money },
  rpm: { header: "RPM", format: optional },
  cpm: { header: "CPM", format: optional },
  rpl: { header: "RPL", format: optional },
  cpl: { header: "CPL", format: optional },
  ecpm: { header: "ECPM", format: optional },
};

const RATIO_COLUMNS = ["rpm", "cpm", "rpl", "cpl", "ecpm"];

const columns = computed(() =>
  (store.daily.columns ?? [])
    .filter((field) => COLUMNS[field])
    .map((field) => ({ field, ...COLUMNS[field] }))
);

const ratioNote = computed(() => {
  const shown = columns.value
    .map((column) => column.field)
    .filter((field) => RATIO_COLUMNS.includes(field))
    .map((field) => COLUMNS[field].header);

  if (!shown.length) return "";

  const list =
    shown.length > 1
      ? `${shown.slice(0, -1).join(", ")} and ${shown.at(-1)}`
      : shown[0];

  return `${list} ${
    shown.length > 1 ? "are" : "is"
  } blank when their denominator is zero.`;
});

const onTableChange = (event) => {
  rowsPerPage.value = event.rows ?? rowsPerPage.value;

  store.loadDaily({
    page: event.page ?? 0,
    rows: rowsPerPage.value,
    sortField: event.sortField,
    sortOrder: event.sortOrder,
  });
};
</script>

<style scoped>
.chart-empty {
  display: flex;
  align-items: center;
  min-height: 300px;
}

:deep(.p-datatable .p-column-title) {
  white-space: nowrap;
}
</style>
